import { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { applyDiscountsAPI, previewDiscountsAPI } from "../../../utils/api/menu";
import { DISCOUNT_TYPES, formatDiscount, pricingLabel } from "../../../utils/menuPricing";
import { Button, Checkbox, Input, NoticeBox, Radio, SearchCheckList, Select } from "../../ui";
import { menuErrorMessage } from "../menuRole";
import { topActionsText } from "./topActionsText";
import { ActionDialog, ItemPicker, MenuPicker, RestaurantPicker, SectionPicker, bothNames, findItem, findSection, itemOptionLabel, sectionOptionLabel, useCopyRestaurants, useDropInvalid, useMenuDetail, useRestaurantMenus } from "./shared";

const nameKey = (name) => String(name || "").trim().toLowerCase();
const discountKey = (d) => (d ? `${d.type}:${Number(d.value)}` : "");
const parseDiscountKey = (key) => {
  const [type, value] = String(key).split(":");
  return { type, value: Number(value) };
};

// Discount → Add / Update / Remove / Copy as ONE backend bulk operation.
//   Menu → scope (items / sections / entire menu) → pricing name(s)
//   → discount (add/update) or source pricing entry (copy)
// Copy picks both sides by Restaurant first (source Restaurant → Menu →
// Section → Item → pricing entry; destination Restaurant → Menu → scope):
// only the discount type/value is copied, destination prices never change.
//   → Preview (backend: affected / skipped with reasons) → Apply
// Discount math and eligibility are decided by the backend preview; this
// component never calculates prices itself.
export default function DiscountActionDialog({ operation, menus, restaurant, initialMenuId, onClose, onDone }) {
  const { can } = useAccess();
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const isCopy = operation === "copy";
  const currentRestaurantId = restaurant?._id ? String(restaurant._id) : "";
  const [srcRestaurantId, setSrcRestaurantId] = useState(currentRestaurantId);
  const [destRestaurantId, setDestRestaurantId] = useState(currentRestaurantId);
  const copyRestaurants = useCopyRestaurants(restaurant, isCopy);
  const srcMenus = useRestaurantMenus(isCopy ? srcRestaurantId : "", { currentRestaurantId, currentMenus: menus, includeArchived: true });
  const destMenus = useRestaurantMenus(isCopy ? destRestaurantId : "", { currentRestaurantId, currentMenus: menus });
  const crossRestaurant = isCopy && Boolean(srcRestaurantId && destRestaurantId) && srcRestaurantId !== destRestaurantId;
  // Destination: a menu that can still be changed. Copy source: any menu of
  // the restaurant (picked separately, defaults to the open one).
  const isEditable = (id) => menus.some((m) => String(m._id) === String(id) && m.lifecycle !== "archived");
  const [menuId, setMenuId] = useState(isEditable(initialMenuId) ? initialMenuId : "");
  const [sourceMenuId, setSourceMenuId] = useState(initialMenuId || "");
  const [scope, setScope] = useState("items");
  const [sectionFilter, setSectionFilter] = useState("");
  const [itemIds, setItemIds] = useState([]);
  const [sectionIds, setSectionIds] = useState([]);
  const [pricingNames, setPricingNames] = useState([]);
  const [discount, setDiscount] = useState({ type: "percentage", value: "" });
  const [overwrite, setOverwrite] = useState(false);
  const [matchKey, setMatchKey] = useState("");
  const [source, setSource] = useState({ sectionId: "", itemId: "", pricingName: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const { menu, loading } = useMenuDetail(menuId);
  const sections = useMemo(() => menu?.sections || [], [menu]);
  const allItems = useMemo(() => sections.flatMap((s) => (s.items || []).map((item) => ({ item, section: s }))), [sections]);

  // ------------------------------------------------------------- cascading
  const selectMenu = (id) => {
    setMenuId(id);
    setSectionFilter("");
    setItemIds([]);
    setSectionIds([]);
    setPricingNames([]);
    setMatchKey("");
    setPreview(null);
    setError("");
  };
  const liveSectionIds = useMemo(() => sections.map((s) => String(s._id)), [sections]);
  const liveItemIds = useMemo(() => allItems.map(({ item }) => String(item._id)), [allItems]);
  // After a (re)load, drop anything that no longer exists in this menu.
  useEffect(() => {
    if (!menu) return;
    setItemIds((ids) => ids.filter((id) => liveItemIds.includes(id)));
    setSectionIds((ids) => ids.filter((id) => liveSectionIds.includes(id)));
  }, [menu, liveItemIds, liveSectionIds]);
  useDropInvalid(sectionFilter, liveSectionIds, () => setSectionFilter(""), Boolean(menu));

  const visibleItemOptions = useMemo(
    () => allItems.filter(({ section }) => !sectionFilter || String(section._id) === String(sectionFilter)).map(({ item, section }) => ({ value: String(item._id), label: sectionFilter ? itemOptionLabel(item) : `${itemOptionLabel(item)} — ${bothNames(section.name)}` })),
    [allItems, sectionFilter],
  );
  // Changing the section filter drops selections the list no longer shows.
  const changeSectionFilter = (id) => {
    setSectionFilter(id);
    const shown = new Set(allItems.filter(({ section }) => !id || String(section._id) === String(id)).map(({ item }) => String(item._id)));
    setItemIds((ids) => ids.filter((x) => shown.has(x)));
  };

  // Items the chosen scope covers (for pricing-name options and counts).
  const targetItems = useMemo(() => {
    if (scope === "menu") return allItems;
    if (scope === "sections") return allItems.filter(({ section }) => sectionIds.includes(String(section._id)));
    return allItems.filter(({ item }) => itemIds.includes(String(item._id)));
  }, [scope, allItems, sectionIds, itemIds]);

  // Pricing names come from the data (flexible names), counted per item.
  const pricingOptions = useMemo(() => {
    const pool = targetItems.length ? targetItems : allItems;
    const byKey = new Map();
    for (const { item } of pool) {
      for (const entry of item.pricing || []) {
        const key = nameKey(entry.name);
        const current = byKey.get(key) || { value: entry.name, count: 0, discounted: 0 };
        current.count += 1;
        if (entry.discount) current.discounted += 1;
        byKey.set(key, current);
      }
    }
    return [...byKey.values()].sort((a, b) => b.count - a.count).map((o) => ({ value: o.value, label: `${pricingLabel(o.value, lang)} (${o.count})`, count: o.count, discounted: o.discounted }));
  }, [targetItems, allItems, lang]);
  const pricingValues = useMemo(() => pricingOptions.map((o) => o.value), [pricingOptions]);
  useEffect(() => {
    if (menu) setPricingNames((names) => names.filter((n) => pricingValues.some((v) => nameKey(v) === nameKey(n))));
  }, [menu, pricingValues]);

  // Update: the discounts currently on the targeted entries (a filter, not math).
  const currentDiscountOptions = useMemo(() => {
    const keys = new Set(pricingNames.map(nameKey));
    const counts = new Map();
    for (const { item } of targetItems) {
      for (const entry of item.pricing || []) {
        if (!keys.has(nameKey(entry.name)) || !entry.discount) continue;
        const key = discountKey(entry.discount);
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    return [...counts.entries()].map(([key, count]) => ({ value: key, label: t.onlyDiscount(formatDiscount(parseDiscountKey(key), lang), count) }));
  }, [targetItems, pricingNames, lang, t]);
  useDropInvalid(matchKey, currentDiscountOptions.map((o) => o.value), () => setMatchKey(""), Boolean(menu));

  // Copy source: Section → Item → a pricing entry that has a discount.
  const selectSrcRestaurant = (id) => {
    setSrcRestaurantId(id);
    selectSourceMenu("");
  };
  const selectDestRestaurant = (id) => {
    setDestRestaurantId(id);
    selectMenu("");
  };
  const sourceDetail = useMenuDetail(operation === "copy" && String(sourceMenuId) !== String(menuId) ? sourceMenuId : "");
  const sourceMenu = operation !== "copy" ? null : String(sourceMenuId) === String(menuId) ? menu : sourceDetail.menu;
  const sourceLoading = String(sourceMenuId) === String(menuId) ? loading : sourceDetail.loading;
  const sourceSectionIds = useMemo(() => (sourceMenu?.sections || []).map((s) => String(s._id)), [sourceMenu]);
  const selectSourceMenu = (id) => {
    setSourceMenuId(id);
    setSource({ sectionId: "", itemId: "", pricingName: "" });
    setPreview(null);
  };
  const sourceSection = findSection(sourceMenu, source.sectionId);
  const sourceItem = findItem(sourceSection, source.itemId);
  const sourceEntries = (sourceItem?.pricing || []).filter((p) => p.discount);
  const sourceEntry = sourceEntries.find((p) => nameKey(p.name) === nameKey(source.pricingName));
  useDropInvalid(source.sectionId, sourceSectionIds, () => setSource({ sectionId: "", itemId: "", pricingName: "" }), Boolean(sourceMenu));
  useDropInvalid(source.itemId, (sourceSection?.items || []).map((i) => String(i._id)), () => setSource((s) => ({ ...s, itemId: "", pricingName: "" })), Boolean(sourceSection));

  // ------------------------------------------------------------ request
  const needsDiscount = operation === "add" || operation === "update";
  const discountValue = Number(discount.value);
  const discountProblem = !needsDiscount
    ? ""
    : discount.value === "" || !Number.isFinite(discountValue) || discountValue < 0
      ? t.invalidDiscount
      : discount.type === "percentage" && discountValue > 100
        ? t.percentMax
        : "";
  const scopeReady = scope === "menu" || (scope === "sections" ? sectionIds.length > 0 : itemIds.length > 0);
  const effectiveNames = operation === "copy" && !pricingNames.length && sourceEntry ? [sourceEntry.name] : pricingNames;
  const ready = Boolean(menuId && menu && scopeReady && effectiveNames.length && !discountProblem && (operation !== "copy" || sourceEntry));

  const request = useMemo(
    () => ({
      operation,
      scope,
      itemIds: scope === "items" ? itemIds : [],
      sectionIds: scope === "sections" ? sectionIds : [],
      pricingNames: effectiveNames,
      ...(needsDiscount ? { discount: { type: discount.type, value: discountValue } } : {}),
      ...(operation === "update" && matchKey ? { matchDiscount: parseDiscountKey(matchKey) } : {}),
      ...(operation === "add" || operation === "copy" ? { overwrite } : {}),
      ...(operation === "copy" && sourceEntry ? { restaurantId: destRestaurantId, source: { restaurantId: srcRestaurantId, menuId: sourceMenuId, sectionId: source.sectionId, itemId: source.itemId, pricingName: sourceEntry.name } } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operation, scope, itemIds, sectionIds, effectiveNames.join("|"), discount.type, discountValue, matchKey, overwrite, menuId, sourceMenuId, source.sectionId, source.itemId, sourceEntry?.name, srcRestaurantId, destRestaurantId],
  );
  const requestKey = JSON.stringify([menuId, request]);
  const stale = preview && preview.key !== requestKey;

  const runPreview = async () => {
    if (busy || !ready) return;
    setBusy("preview");
    setError("");
    try {
      const result = await previewDiscountsAPI(menuId, request);
      setPreview({ ...result, key: requestKey });
    } catch (err) {
      setPreview(null);
      setError(menuErrorMessage(err, lang));
    } finally {
      setBusy("");
    }
  };

  const apply = async () => {
    if (busy || !preview || stale || !preview.affectedCount) return;
    if (!can("menu.manage", { restaurantId: restaurant?.id })) return;

    setBusy("apply");
    setError("");
    try {
      const result = await applyDiscountsAPI(menuId, { ...request, expectedVersion: preview.menuVersion });
      onDone({ menuId, menu: result.menu, message: t.discountApplied(result.result.affectedCount, result.result.skippedCount) });
    } catch (err) {
      setError(menuErrorMessage(err, lang));
      // The menu may have changed: the preview is no longer trustworthy.
      if (err?.code === "MENU_CHANGED") setPreview(null);
    } finally {
      setBusy("");
    }
  };

  const editable = (isCopy ? destMenus.menus : menus).filter((m) => m.lifecycle !== "archived");
  const scopeName = `discount-scope-${operation}`;

  return (
    <ActionDialog
      title={t.opTitle[operation]}
      busy={Boolean(busy)}
      error={error}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="primary" appearance="soft" fullWidth rounded loading={busy === "preview"} disabled={!ready || Boolean(busy)} onClick={runPreview}>
            {t.preview}
          </Button>
          <Button type="button" variant={operation === "remove" ? "danger" : "primary"} appearance="filled" fullWidth rounded loading={busy === "apply"} disabled={!preview || stale || !preview.affectedCount || Boolean(busy)} onClick={apply}>
            {t.applyTo(preview && !stale ? preview.affectedCount : 0)}
          </Button>
          <Button type="button" variant="warning" appearance="soft" fullWidth rounded disabled={Boolean(busy)} onClick={onClose}>
            {t.cancel}
          </Button>
        </>
      }
    >
      {!isCopy && !editable.length && <NoticeBox variant="neutral" size="sm">{t.noMenus}</NoticeBox>}
      {operation === "copy" && (
        <fieldset className="menu-action-fieldset">
          <legend>{t.source}</legend>
          <div className="menu-action-pickers">
            <RestaurantPicker label={t.sourceRestaurant} restaurants={copyRestaurants.restaurants} loading={copyRestaurants.loading} value={srcRestaurantId} onChange={selectSrcRestaurant} disabled={Boolean(busy)} />
            <MenuPicker label={t.sourceMenu} menus={srcMenus.menus} value={sourceMenuId} onChange={selectSourceMenu} disabled={Boolean(busy) || srcMenus.loading} />
            <SectionPicker label={t.sourceSection} menu={sourceMenu} loading={sourceLoading} value={source.sectionId} onChange={(id) => setSource({ sectionId: id, itemId: "", pricingName: "" })} />
            <ItemPicker label={t.sourceItem} section={sourceSection} value={source.itemId} onChange={(id) => setSource((s) => ({ ...s, itemId: id, pricingName: "" }))} />
            <Select
              label={t.sourcePricing}
              placeholder={t.selectPricing}
              value={sourceEntry ? sourceEntry.name : ""}
              disabled={!sourceEntries.length}
              options={sourceEntries.map((p) => ({ value: p.name, label: `${pricingLabel(p.name, lang)} · ${p.price} (−${formatDiscount(p.discount, lang)})` }))}
              onChange={(e) => setSource((s) => ({ ...s, pricingName: e.target.value }))}
            />
          </div>
        </fieldset>
      )}

      {isCopy ? (
        <fieldset className="menu-action-fieldset">
          <legend>{t.destination}</legend>
          <div className="menu-action-pickers">
            <RestaurantPicker label={t.destinationRestaurant} restaurants={copyRestaurants.restaurants} loading={copyRestaurants.loading} value={destRestaurantId} onChange={selectDestRestaurant} disabled={Boolean(busy)} />
            <MenuPicker label={t.destinationMenu} menus={editable} value={menuId} onChange={selectMenu} disabled={Boolean(busy) || destMenus.loading} />
          </div>
          {destRestaurantId && !destMenus.loading && !editable.length && <NoticeBox variant="neutral" size="sm">{t.noMenusInRestaurant}</NoticeBox>}
          {crossRestaurant && <p className="menu-note">{t.discountCrossNote}</p>}
        </fieldset>
      ) : (
        <MenuPicker label={t.menu} menus={editable} value={menuId} onChange={selectMenu} disabled={Boolean(busy)} />
      )}

      {menu && (
        <fieldset className="menu-action-fieldset">
          <legend>{operation === "copy" ? `${t.destination} · ${t.scope}` : t.scope}</legend>
          <div className="menu-action-radios" role="radiogroup" aria-label={t.scope}>
            <Radio name={scopeName} value="items" checked={scope === "items"} onChange={() => setScope("items")} label={t.scopeItems} />
            <Radio name={scopeName} value="sections" checked={scope === "sections"} onChange={() => setScope("sections")} label={t.scopeSections} />
            <Radio name={scopeName} value="menu" checked={scope === "menu"} onChange={() => setScope("menu")} label={t.scopeMenu} />
          </div>

          {scope === "items" && (
            <>
              <Select label={t.sectionFilter} value={sectionFilter || "all"} options={[{ value: "all", label: t.allSections }, ...sections.map((s, i) => ({ value: String(s._id), label: sectionOptionLabel(s, i) }))]} onChange={(e) => changeSectionFilter(e.target.value === "all" ? "" : e.target.value)} />
              <SearchCheckList label={t.items} options={visibleItemOptions} selected={itemIds} onChange={setItemIds} placeholder={t.selectItem} fullWidth="full-width" />
              <div className="menu-action-inline">
                <span className="menu-secondary">{t.selectedCount(itemIds.length)}</span>
                <Button type="button" size="xs" appearance="soft" rounded onClick={() => setItemIds([...new Set([...itemIds, ...visibleItemOptions.map((o) => o.value)])])}>
                  {t.selectAll}
                </Button>
                {itemIds.length > 0 && (
                  <Button type="button" size="xs" variant="warning" appearance="soft" rounded onClick={() => setItemIds([])}>
                    {t.clear}
                  </Button>
                )}
              </div>
            </>
          )}
          {scope === "sections" && (
            <>
              <SearchCheckList label={t.sections} options={sections.map((s, i) => ({ value: String(s._id), label: sectionOptionLabel(s, i) }))} selected={sectionIds} onChange={setSectionIds} placeholder={t.selectSection} fullWidth="full-width" />
              <div className="menu-action-inline">
                <span className="menu-secondary">{t.selectedCount(sectionIds.length)}</span>
                <Button type="button" size="xs" appearance="soft" rounded onClick={() => setSectionIds(liveSectionIds)}>
                  {t.selectAll}
                </Button>
              </div>
            </>
          )}
          {scope === "menu" && <p className="menu-note">{t.wholeMenuNote(allItems.length)}</p>}
        </fieldset>
      )}

      {menu && (
        <fieldset className="menu-action-fieldset">
          <legend>{t.pricingNames}</legend>
          <SearchCheckList options={pricingOptions} selected={effectiveNames.map((n) => pricingValues.find((v) => nameKey(v) === nameKey(n)) || n)} onChange={setPricingNames} placeholder={t.selectPricing} fullWidth="full-width" />
          <p className="menu-note">{t.pricingNamesHint}</p>

          {needsDiscount && (
            <div className="menu-action-pickers">
              <Select label={t.discountType} value={discount.type} options={DISCOUNT_TYPES.map((type) => ({ value: type, label: t[type] }))} onChange={(e) => setDiscount((d) => ({ ...d, type: e.target.value }))} />
              <Input label={t.discountValue} type="number" min="0" step="0.01" max={discount.type === "percentage" ? 100 : undefined} value={discount.value} error={discount.value !== "" ? discountProblem : ""} onChange={(e) => setDiscount((d) => ({ ...d, value: e.target.value }))} />
            </div>
          )}
          {operation === "update" && currentDiscountOptions.length > 0 && (
            <Select label={t.matchDiscount} value={matchKey || "any"} options={[{ value: "any", label: t.anyDiscount }, ...currentDiscountOptions]} onChange={(e) => setMatchKey(e.target.value === "any" ? "" : e.target.value)} />
          )}
          {(operation === "add" || operation === "copy") && <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} label={t.overwrite} />}
        </fieldset>
      )}

      {stale && (
        <NoticeBox variant="warning" size="sm">
          {t.previewStale}
        </NoticeBox>
      )}
      {preview && !stale && <DiscountPreview preview={preview} operation={operation} menuName={menu?.name} />}
    </ActionDialog>
  );
}

// Backend preview: counts, reasons, and per-item before → after prices.
function DiscountPreview({ preview, operation, menuName }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const affected = preview.items.filter((i) => i.status === "affected");
  const skipped = preview.items.filter((i) => i.status === "skipped");
  const discountText = operation === "remove" ? t.noneDiscount : formatDiscount(preview.discount, lang);
  const scopeText = { items: t.scopeItems, sections: t.scopeSections, menu: t.scopeMenu }[preview.scope];
  return (
    <section className="menu-bulk-preview" aria-label={t.previewTitle}>
      <h4>
        {t.opTitle[operation]}: {preview.pricingNames.map((n) => `"${pricingLabel(n, lang)}"`).join(", ")} → {discountText}
      </h4>
      {operation === "copy" && preview.source && (
        <p className="menu-secondary">
          {t.source}: {preview.source.restaurantName} · {preview.source.menuName} · {bothNames(preview.source.itemName)}
        </p>
      )}
      <p className="menu-secondary">
        {operation === "copy" && preview.destination ? `${preview.destination.restaurantName} · ` : ""}
        {t.menu}: {menuName} · {t.scope}: {scopeText}
      </p>
      <dl className="menu-bulk-stats">
        <div>
          <dt>{t.itemsFound}</dt>
          <dd>{preview.itemsFound}</dd>
        </div>
        <div className="is-good">
          <dt>{t.eligible}</dt>
          <dd>{preview.affectedCount}</dd>
        </div>
        <div className={preview.skippedCount ? "is-warn" : ""}>
          <dt>{t.skipped}</dt>
          <dd>{preview.skippedCount}</dd>
        </div>
      </dl>
      {preview.currentDiscounts?.length > 0 && (
        <p className="menu-secondary">
          {t.currentDiscounts}: {preview.currentDiscounts.map((d) => `${formatDiscount(d, lang)} (${d.count})`).join(" · ")}
          {operation !== "remove" && ` → ${t.newDiscount}: ${discountText}`}
        </p>
      )}
      {Object.keys(preview.skippedByReason || {}).length > 0 && (
        <ul className="menu-bulk-reasons">
          {Object.entries(preview.skippedByReason).map(([reason, count]) => (
            <li key={reason}>
              {t.reasons[reason] || reason}: <strong>{count}</strong>
            </li>
          ))}
        </ul>
      )}
      {affected.length > 0 && (
        <details>
          <summary>
            {t.showAffected} ({affected.length})
          </summary>
          <ul className="menu-bulk-list">
            {affected.map((entry) => (
              <li key={entry.itemId}>
                <strong>{bothNames(entry.itemName)}</strong>
                {entry.entries
                  .filter((e) => !e.skip)
                  .map((e) => (
                    <span key={e.pricingName} className="menu-secondary">
                      {" "}
                      · {pricingLabel(e.pricingName, lang)}: {e.effectiveBefore} → {e.effectiveAfter}
                    </span>
                  ))}
              </li>
            ))}
          </ul>
        </details>
      )}
      {skipped.length > 0 && (
        <details>
          <summary>
            {t.showSkipped} ({skipped.length})
          </summary>
          <ul className="menu-bulk-list">
            {skipped.map((entry) => (
              <li key={entry.itemId}>
                <strong>{bothNames(entry.itemName)}</strong>
                <span className="menu-secondary"> — {entry.reasons.map((r) => t.reasons[r] || r).join(", ")}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
